import * as React from "react";
import { styled } from "styled-components";
import type { Item } from "@wayflyer/flyui";
import { Text, DropdownField } from "@wayflyer/flyui";
import { TemporalResolutionPickers } from "@wayflyer/flyui-dataviz";
// eslint-disable-next-line no-restricted-imports
import moment from "moment";

import { DateRangeSelectorContainer, DateRangeComboContainer } from "../../../../components/styled";

export interface DateRangeProps {
  currentDateRange: string;
  onChange: (id: string) => void;
  dateRangeOptions: Item[];
}

export interface ConsumersHeaderRowProps {
  startDatePicker: DateRangeProps;
  endDatePicker: DateRangeProps;
  temporalResOptions: Item[];
  selectedTempResIndex: number;
  onClickTemporalRes: (res: string) => void;
}

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 768px) {
    flex-direction: row;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: space-between;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  @media (max-width: 768px) {
    flex-direction: column;
    overflow-x: auto;
    align-items: start;
  }
`;

const SmallSpacer = styled.span`
  margin: 5px;
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const ResolutionPickerDiv = styled.div`
  padding: 12px 0;
`;

const ContentContainer = styled.div`
  width: 100%;
  margin: auto;
  padding-top: 24px;
  color: var(--palette-content-neutral-strong);
  @media (max-width: 640px) {
    padding-top: 20px;
  }
`;

/**
 * ConsumersHeaderRow component
 */
export const ConsumersHeaderRow = ({
  startDatePicker,
  endDatePicker,
  temporalResOptions,
  selectedTempResIndex,
  onClickTemporalRes,
}: ConsumersHeaderRowProps): UIElement => {
  return (
    <ContentContainer>
      <HeaderRow>
        <MonthRangeSelector startDatePicker={startDatePicker} endDatePicker={endDatePicker} />
        <ResolutionPickerDiv>
          <TemporalResolutionPickers
            selectedButtonIndex={selectedTempResIndex}
            options={temporalResOptions}
            onClickDateRes={onClickTemporalRes}
          />
        </ResolutionPickerDiv>
      </HeaderRow>
    </ContentContainer>
  );
};

const getSelectedOption = (options: Item[], selectedMonthId: string): string | null => {
  const selectedMonthMoment = moment(selectedMonthId, "YYYY - M");
  return options.reduce((selOption: string | null, option: Item) => {
    const optionMoment = moment(option.id, "YYYY - M");
    if (optionMoment <= selectedMonthMoment) {
      selOption = option.id;
    }
    return selOption;
  }, null);
};

const MonthRangeSelector = ({ startDatePicker, endDatePicker }): UIElement => {
  const { currentDateRange: startCurrent, dateRangeOptions: startOptions, onChange: startOnChange } = startDatePicker;
  const { currentDateRange: endCurrent, dateRangeOptions: endOptions, onChange: endOnChange } = endDatePicker;
  const [selectedStartOption, setSelectedStartOption] = React.useState<string | null>(
    getSelectedOption(startOptions, startCurrent)
  );
  const [selectedEndOption, setSelectedEndOption] = React.useState<string | null>(
    getSelectedOption(endOptions, endCurrent)
  );

  const onStartChange = (id: string) => {
    setSelectedStartOption(id);
    startOnChange(id);
  };

  const onEndChange = (id: string) => {
    setSelectedEndOption(id);
    endOnChange(id);
  };

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setSelectedStartOption(getSelectedOption(startOptions, startCurrent));
    setSelectedEndOption(getSelectedOption(endOptions, endCurrent));
  }, [startCurrent, endCurrent, startOptions, endOptions]);

  const StartDropDown = (
    <DateRangeSelectorContainer>
      <DateRangeComboContainer useSimplePanel={true} width="150px">
        <DropdownField
          initialValue={startCurrent}
          disabled={false}
          readonly={false}
          name="chart-type"
          options={startOptions}
          onChange={onStartChange}
          icon="calendar"
          controlledValue={selectedStartOption || undefined}
        />
      </DateRangeComboContainer>
    </DateRangeSelectorContainer>
  );

  const EndDropDown = (
    <DateRangeSelectorContainer>
      <DateRangeComboContainer useSimplePanel={true} width="150px">
        <DropdownField
          initialValue={endCurrent}
          disabled={false}
          readonly={false}
          name="chart-type"
          options={endOptions}
          onChange={onEndChange}
          controlledValue={selectedEndOption || undefined}
          icon="calendar"
        />
      </DateRangeComboContainer>
    </DateRangeSelectorContainer>
  );
  return (
    <Container>
      <ColumnContainer>
        <Text>Start</Text>
        {StartDropDown}
      </ColumnContainer>
      <SmallSpacer />
      <ColumnContainer>
        <Text>End</Text>
        {EndDropDown}{" "}
      </ColumnContainer>
    </Container>
  );
};
